import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, DatePicker, Table, Spin, Select, message } from "antd";
import dayjs from "dayjs";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import locale from "antd/es/date-picker/locale/zh_CN";
import announcement_icon from "@/images/service/announcement_icon.png";
import { getDictionaryList } from "@/apis/commonApi";
import empty_png from "@/images/empty.png";
import "./index.less";

import "dayjs/locale/zh-cn";
import { serviceApi } from "@/apis";
import { useUserInfo } from "@/model";
const { RangePicker } = DatePicker;

const getDictCodeItem = (keyList: DictionarySelectOption[] = [], type: string) => {
  const _index = keyList.findIndex(el => el.value === type);
  return _index !== -1 ? keyList[_index].label : "";
}

const Announcement = () => {
  const userInfo = useUserInfo();
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<NoticeListItem[]>([]);
  // 搜索条件
  const [type, setType] = useState<string>();
  const [rangeTime, setRangeTime] = useState<any>();
  const [noticeState, setNoticeState] = useState<string>();
  const [haveRead, setHaveRead] = useState<string>();

  // 搜索条件下拉框
  const [typeOption, setTypeOption] = useState<DictionarySelectOption[]>();
  const [stateOption, setStateOption] = useState<DictionarySelectOption[]>();
  const [msgstateOption, setMsgstateOption] =
    useState<DictionarySelectOption[]>();

  // 表格信息
  const [curPage, setCurPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    getDictionaryList({
      dictTypeCodeList: ["notice_type", "notice_state", "notice_msg_state"],
    })
      .then((res) => {
        const _typeOption =
          res?.notice_type.map(({ dictCode, dictValue }) => ({
            value: dictCode,
            label: dictValue,
          })) || [];
        const _stateOption =
          res?.notice_state.map(({ dictCode, dictValue }) => ({
            value: dictCode,
            label: dictValue,
          })) || [];
        const _msgstateOption =
          res?.notice_msg_state.map(({ dictCode, dictValue }) => ({
            value: dictCode,
            label: dictValue,
          })) || [];
        setTypeOption(_typeOption);
        setStateOption(_stateOption);
        setMsgstateOption(_msgstateOption);
      })
      .catch((err) => {
        message.error(err);
      });
  }, []);

  useEffect(() => {
    if (userInfo.curUserInfo) {
      handleSearch({
        account: userInfo.curUserInfo.userAccount,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.curUserInfo]);

  const handleSearch = async (data: any = {}) => {
    try {
      setLoading(true);
      // const kprq = orderTime && dayjs(orderTime).format("YYYYMM");
      const res = await serviceApi.findByNoticeCustomerPageDto({
        account: userInfo.curUserInfo?.userAccount,
        type,
        noticeState,
        haveRead,
        startTime:
          rangeTime && dayjs(rangeTime[0]).format("YYYY-MM-DD HH:mm:ss"),
        endTime: rangeTime && dayjs(rangeTime[1]).format("YYYY-MM-DD HH:mm:ss"),
        size: 20,
        page: curPage,
        ...data,
      });
      const { total, records } = res;
      setTotal(total);
      setTableData(records);
    } catch (error: any) {
      message.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInquire = () => {
    setCurPage(1);
    handleSearch({ page: 1 });
  };

  const handleReset = () => {
    setCurPage(1);
    setType(undefined);
    setRangeTime([]);
    setNoticeState(undefined);
    setHaveRead(undefined);
    handleSearch({
      page: 1,
      type: "",
      noticeState: "",
      haveRead: "",
      startTime: "",
      endTime: "",
    });
  };

  const changePage = (page: number) => {
    setCurPage(page);
    handleSearch({ page });
  };

  const columns: ColumnsType<NoticeListItem> = useMemo(
    () => [
      {
        title: "公告名称",
        dataIndex: "title",
        key: "title",
      },
      {
        title: "公告类型",
        dataIndex: "type",
        key: "type",
        render: (type) => <span>{getDictCodeItem(typeOption, type)}</span>,
      },
      {
        title: "发布日期",
        dataIndex: "startTime",
        key: "startTime",
      },
      {
        title: "结束日期",
        dataIndex: "endTime",
        key: "endTime",
      },
      {
        title: "有效期状态",
        dataIndex: "noticeState",
        key: "noticeState",
        render: (noticeState) => <span>{getDictCodeItem(stateOption, noticeState)}</span>,
      },
      {
        title: "消息状态",
        dataIndex: "haveRead",
        key: "haveRead",
        render: (haveRead) => <span>{getDictCodeItem(msgstateOption, `${haveRead}`)}</span>,
      },
      {
        title: "操作",
        key: "action",
        render: (_, record) => (
          <Link to={`/service/announcement/detail`} state={{ id: record.id }}>
            查看
          </Link>
        ),
      },
    ],
    [typeOption, stateOption, msgstateOption]
  );

  return (
    <div className="announcement-wrapper">
      <div className="center-main-title">
        <img src={announcement_icon} alt="" />
        <h3>公告查看</h3>
      </div>
      <div className="announcement-table-wrap">
        <Spin spinning={loading}>
          <article className="flex-b-c search-wrapper">
            <div className="flex filter-item">
              <Select
                value={type}
                className="form-item"
                onChange={(e) => {
                  setType(e);
                }}
                allowClear
                placeholder="公告类型"
                options={typeOption}
              />
              <RangePicker
                placeholder={["发布开始日期", "发布结束日期"]}
                value={rangeTime}
                locale={locale}
                format="YYYY-MM-DD"
                className="time-item"
                onChange={(e: any) => setRangeTime(e)}
              />
              <Select
                value={noticeState}
                className="form-item"
                onChange={(e) => {
                  setNoticeState(e);
                }}
                allowClear
                placeholder="有效期类型"
                options={stateOption}
              />
              <Select
                value={haveRead}
                className="form-item"
                onChange={(e) => {
                  setHaveRead(e);
                }}
                allowClear
                placeholder="消息类型"
                options={msgstateOption}
              />
            </div>
            <div className="btns">
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button
                className="inquire-btn"
                icon={<SearchOutlined />}
                type="primary"
                onClick={handleInquire}
              >
                查询
              </Button>
            </div>
          </article>
          <Table
            columns={columns}
            dataSource={tableData}
            className="announcement-table"
            locale={{
              emptyText: (
                <div style={{ marginBottom: 30 }}>
                  <img src={empty_png} alt="暂无数据" />
                  <p>暂无数据</p>
                </div>
              ),
            }}
            pagination={{
              current: curPage,
              total,
              defaultPageSize: 10,
              onChange: changePage,
            }}
          />
        </Spin>
      </div>
    </div>
  );
};

export default Announcement;
